import { useState, useRef, useEffect } from 'react';
import { findUserByEmail, searchUsersByNickname } from '../../services/userService';
import type { User } from '../../types';

interface UserFinderProps {
  onUserSelect: (user: User) => void;
  /** Show email input row (default: true) */
  showEmail?: boolean;
  /** Label width class (default: 'w-14') */
  labelWidth?: string;
  /** Optional: controlled nickname value */
  nickname?: string;
  /** Optional: controlled email value */
  email?: string;
  /** Show selected user info badge (default: true) */
  showSelectedInfo?: boolean;
}

export default function UserFinder({
  onUserSelect,
  showEmail = true,
  labelWidth = 'w-14',
  nickname: controlledNickname,
  email: controlledEmail,
  showSelectedInfo = true,
}: UserFinderProps) {
  const [emailValue, setEmailValue] = useState(controlledEmail ?? '');
  const [nicknameValue, setNicknameValue] = useState(controlledNickname ?? '');
  const [results, setResults] = useState<User[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [searching, setSearching] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Sync controlled props
  useEffect(() => {
    if (controlledNickname !== undefined) setNicknameValue(controlledNickname);
  }, [controlledNickname]);
  useEffect(() => {
    if (controlledEmail !== undefined) setEmailValue(controlledEmail);
  }, [controlledEmail]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function selectUser(user: User) {
    setSelectedUser(user);
    setNicknameValue(user.nickname);
    setEmailValue(user.email);
    setShowDropdown(false);
    onUserSelect(user);
  }

  async function handleFindByNickname() {
    if (!nicknameValue.trim()) return;
    setSearching(true);
    try {
      const found = await searchUsersByNickname(nicknameValue.trim());
      if (found.length === 0) {
        alert(`"${nicknameValue}"을(를) 포함하는 닉네임의 사용자를 찾을 수 없습니다.`);
        setShowDropdown(false);
      } else if (found.length === 1) {
        selectUser(found[0]);
      } else {
        setResults(found);
        setShowDropdown(true);
      }
    } finally {
      setSearching(false);
    }
  }

  async function handleFindByEmail() {
    if (!emailValue.trim()) return;
    setSearching(true);
    try {
      const user = await findUserByEmail(emailValue.trim());
      if (user) selectUser(user);
      else alert(`"${emailValue}" 이메일의 사용자를 찾을 수 없습니다.`);
    } finally {
      setSearching(false);
    }
  }

  function handleClear() {
    setSelectedUser(null);
    setNicknameValue('');
    setEmailValue('');
    setResults([]);
    setShowDropdown(false);
  }

  return (
    <div className="space-y-1.5 text-sm">
      {showEmail && (
        <div className="flex items-center gap-1">
          <label className={`${labelWidth} text-gray-500`}>이메일:</label>
          <input
            value={emailValue}
            onChange={e => { setEmailValue(e.target.value); setSelectedUser(null); }}
            onKeyDown={e => e.key === 'Enter' && handleFindByEmail()}
            placeholder="이메일로 검색"
            className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
          />
          <button
            onClick={handleFindByEmail}
            disabled={searching}
            className="px-2 py-1 text-xs bg-gray-100 border border-gray-300 rounded hover:bg-gray-200 disabled:opacity-50"
          >
            적용
          </button>
        </div>
      )}
      <div className="flex items-center gap-1 relative" ref={dropdownRef}>
        <label className={`${labelWidth} text-gray-500`}>닉네임:</label>
        <input
          value={nicknameValue}
          onChange={e => { setNicknameValue(e.target.value); setShowDropdown(false); setSelectedUser(null); }}
          onKeyDown={e => e.key === 'Enter' && handleFindByNickname()}
          placeholder="닉네임으로 검색"
          className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
        />
        <button
          onClick={handleFindByNickname}
          disabled={searching}
          className="px-2 py-1 text-xs bg-gray-100 border border-gray-300 rounded hover:bg-gray-200 disabled:opacity-50"
        >
          사용자 찾기
        </button>
        {showDropdown && results.length > 0 && (
          <div className="absolute top-full left-14 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-20 max-h-48 overflow-y-auto">
            {results.map(u => (
              <button
                key={u.uid}
                onClick={() => selectUser(u)}
                className="w-full px-3 py-1.5 text-left text-sm hover:bg-blue-50 flex items-center justify-between border-b border-gray-100 last:border-0"
              >
                <span className="font-medium">{u.nickname}</span>
                <span className="text-xs text-gray-400">{u.email}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {showSelectedInfo && selectedUser && (
        <div className="flex items-center gap-2 px-2 py-1 bg-green-50 border border-green-200 rounded text-xs text-green-700">
          <span>선택됨: <strong>{selectedUser.nickname}</strong> ({selectedUser.email})</span>
          <button onClick={handleClear} className="ml-auto text-gray-400 hover:text-red-500">✕</button>
        </div>
      )}
    </div>
  );
}
